import { mapNotificationBadges, mapNotificationBadgesSK } from "../../helpers";
import Time from "../../lib/Time/Time";
import { BeverageTranslation } from "../beers/types";
import {
  IApiBeerDrive,
  IApiPressureChamber,
  IApiSteelKegUnit,
  IApiX10,
  IPressureChamber,
  ISteelKegUnit,
  IX10
} from "./types";
import { X10_KEG_VOLUME_LITRES } from "../../constants";
import { evaluateX10, firmwareLabel, isFirmwareUpdateAvailable } from "../../lib/x10/x10Status";

export const mapPressureChambers = (installation: any) =>
  (installation.pressureChambers || [])
    .map(
      ({
        alive,
        existsLeak,
        faulty: isFaulty = false,
        id,
        keg,
        lastMounted,
        lastUsed,
        name,
        open: isOpen,
        position,
        sensorConnected,
        taps,
        thingId,
        hasPressureSwitch
      }: IApiPressureChamber) => {
        const mappedPressureChamber: IPressureChamber = {
          bestBefore: keg?.bestBefore ? new Time(keg.bestBefore).getTime() : undefined,
          beverage: new BeverageTranslation(),
          beverageId: keg?.beverage?.id,
          existsLeak,
          dangerTags: 0,
          id,
          isBeerDrive: false,
          isFaulty,
          isOpen: sensorConnected && !isFaulty && alive && isOpen,
          lastMounted: new Time(lastMounted).getTime(),
          lastUsed: lastUsed ? new Time(lastUsed).getTime() : 0,
          name: name || "No Name",
          noKeg: false,
          // turn pressure chamber online if it is the only one offline due to being faulty
          offline:
            isFaulty && !alive
              ? installation?.pressureChambers.filter((p: IApiPressureChamber) => !p.alive)
                  .length !== 1
              : !alive,
          parentName: installation.outletName,
          position,
          sensorConnected,
          successTags: 0,
          tags: [],
          tapPosition: taps.map(tp => tp.position).sort(),
          thingId,
          volume: keg?.volume ? keg.volume / 1000 : 0,
          volumeLeft: keg?.volumeLeft ? keg.volumeLeft / 1000 : 0,
          warningTags: 0,
          hasPressureSwitch
        };

        return mapNotificationBadges(mappedPressureChamber, installation.outletCountry);
      }
    )
    .sort((pcA: IPressureChamber, pcB: IPressureChamber) => {
      return pcA.position < pcB.position ? -1 : 1;
    });

export const mapOutletPressureChambers = (
  pressureChambers: any,
  outletName: string,
  country?: string
) =>
  (pressureChambers || [])
    .map(
      ({
        alive,
        existsLeak,
        faulty: isFaulty = false,
        id,
        keg,
        lastMounted,
        lastUsed,
        name,
        open: isOpen,
        position,
        sensorConnected,
        taps,
        thingId,
        hasPressureSwitch
      }: IApiPressureChamber) => {
        const mappedPressureChamber: IPressureChamber = {
          bestBefore: keg?.bestBefore ? new Time(keg.bestBefore).getTime() : undefined,
          beverage: new BeverageTranslation(),
          beverageId: keg?.beverage?.id,
          existsLeak,
          dangerTags: 0,
          id,
          isBeerDrive: false,
          isFaulty,
          isOpen: sensorConnected && !isFaulty && alive && isOpen,
          lastMounted: new Time(lastMounted).getTime(),
          lastUsed: lastUsed ? new Time(lastUsed).getTime() : 0,
          name: name || "No Name",
          noKeg: false,
          // turn pressure chamber online if it is the only one offline due to being faulty
          offline:
            isFaulty && !alive
              ? pressureChambers.filter((p: IApiPressureChamber) => !p.alive).length !== 1
              : !alive,
          parentName: outletName,
          position,
          sensorConnected,
          successTags: 0,
          tags: [],
          tapPosition: taps.map(tp => tp.position).sort(),
          thingId,
          volume: keg?.volume ? keg.volume / 1000 : 0,
          volumeLeft: keg?.volumeLeft ? keg.volumeLeft / 1000 : 0,
          warningTags: 0,
          hasPressureSwitch
        };

        return mapNotificationBadges(mappedPressureChamber, country);
      }
    )
    .sort((pcA: IPressureChamber, pcB: IPressureChamber) => {
      return pcA.position < pcB.position ? -1 : 1;
    });

export const mapOutletBeerDrives = (beerDrives: any, outletName: string) =>
  (beerDrives || [])
    .map(({ alive, id, name, position, tank, taps, thingId }: IApiBeerDrive) => {
      const mappedBeerDrive: IPressureChamber = {
        beverage: new BeverageTranslation(),
        beverageId: tank?.beverage?.id,
        dangerTags: 0,
        id,
        isBeerDrive: true,
        lastMounted: tank?.lastRefill ? new Time(tank.lastRefill).getTime() : 0,
        lastUsed: tank?.lastDraft ? new Time(tank.lastDraft).getTime() : 0,
        name: name || "No Name",
        offline: !alive,
        parentName: outletName,
        position,
        successTags: 0,
        tags: [],
        tapPosition: taps.map(tp => tp.position).sort(),
        thingId,
        volume: tank?.capacity ? tank.capacity / 1000 : 0,
        volumeLeft: tank?.volumeLeft ? tank.volumeLeft / 1000 : 0,
        warningTags: 0
      };

      return mapNotificationBadges(mappedBeerDrive);
    })
    .sort((bdA: IPressureChamber, bdB: IPressureChamber) => {
      return bdA.position < bdB.position ? -1 : 1;
    });

export const mapBeerDrives = (installation: any) =>
  (installation.beerDrives || [])
    .map(({ alive, id, name, position, tank, taps, thingId }: IApiBeerDrive) => {
      const mappedBeerDrive: IPressureChamber = {
        beverage: new BeverageTranslation(),
        beverageId: tank?.beverage?.id,
        dangerTags: 0,
        id,
        isBeerDrive: true,
        lastMounted: tank?.lastRefill ? new Time(tank.lastRefill).getTime() : 0,
        lastUsed: tank.lastDraft ? new Time(tank.lastDraft).getTime() : 0,
        name: name || "No Name",
        offline: !alive,
        parentName: installation.outletName,
        position,
        successTags: 0,
        tags: [],
        tapPosition: taps.map(tp => tp.position).sort(),
        thingId,
        volume: tank?.capacity ? tank.capacity / 1000 : 0,
        volumeLeft: tank?.volumeLeft ? tank.volumeLeft / 1000 : 0,
        warningTags: 0
      };

      return mapNotificationBadges(mappedBeerDrive);
    })
    .sort((bdA: IPressureChamber, bdB: IPressureChamber) => {
      return bdA.position < bdB.position ? -1 : 1;
    });

export const mapOutletSteelKegUnits = (steelKegUnits: any, outletName: string) =>
  (steelKegUnits || [])
    .map(({ alive, id, name, position, steelKeg, taps, thingId }: IApiSteelKegUnit) => {
      const mappedSteelKegUnit: ISteelKegUnit = {
        beverage: new BeverageTranslation(),
        beverageId: steelKeg?.beverage?.id,
        steelKeg,
        dangerTags: 0,
        id,
        lastUsed: steelKeg?.lastDraft ? new Time(steelKeg.lastDraft).getTime() : 0,
        name: name || "No Name",
        offline: !alive,
        parentName: outletName,
        position,
        successTags: 0,
        tags: [],
        tapPosition: taps.map(tp => tp.position).sort(),
        thingId,
        warningTags: 0
      };

      return mapNotificationBadgesSK(mappedSteelKegUnit);
    })
    .sort((bdA: ISteelKegUnit, bdB: ISteelKegUnit) => {
      return bdA.position < bdB.position ? -1 : 1;
    });

export const mapSteelKegUnits = (installation: any) =>
  (installation.steelKegUnits || [])
    .map(({ alive, id, name, position, steelKeg, taps, thingId }: IApiSteelKegUnit) => {
      const mappedSteelKegUnit: ISteelKegUnit = {
        beverage: new BeverageTranslation(),
        beverageId: steelKeg?.beverage?.id,
        dangerTags: 0,
        id,
        lastUsed: steelKeg.lastDraft ? new Time(steelKeg.lastDraft).getTime() : 0,
        name: name || "No Name",
        steelKeg,
        offline: !alive,
        parentName: installation.outletName,
        position,
        successTags: 0,
        tags: [],
        tapPosition: taps.map(tp => tp.position).sort(),
        thingId,
        warningTags: 0
      };

      return mapNotificationBadgesSK(mappedSteelKegUnit);
    })
    .sort((bdA: ISteelKegUnit, bdB: ISteelKegUnit) => {
      return bdA.position < bdB.position ? -1 : 1;
    });

const toMs = (value?: string | null) => {
  if (!value) {
    return undefined;
  }
  const ms = Date.parse(value);
  return Number.isNaN(ms) ? undefined : ms;
};

export const x10FallbackName = (serialNumber?: string | null) =>
  serialNumber ? `X10 ····${serialNumber.slice(-4)}` : "X10";

export const mapX10s = (apiX10s: IApiX10[] | undefined, outletName: string, now = Date.now()) =>
  (apiX10s || []).map((x10: IApiX10): IX10 => {
    const keg = x10.keg || undefined;
    const mapped = {
      isX10: true as const,
      id: x10.id,
      thingId: x10.thingId,
      name: x10.name || x10FallbackName(x10.boardSerialNumber),
      serialNumber: x10.boardSerialNumber || undefined,
      parentName: outletName,
      createdAt: toMs(x10.createDateTime),
      alive: x10.alive,
      offline: !x10.alive,
      open: x10.open,
      leak: x10.leak,
      hardwareError: x10.hardwareError,
      highTemperatureVessel: x10.highTemperatureVessel,
      enhancedCoolingRunning: x10.enhancedCoolingRunning,
      nfcError: x10.nfcError,
      vesselTemperature: x10.vesselTemperature ?? undefined,
      ambientTemperature: x10.ambientTemperature ?? undefined,
      temperatureReadAt: toMs(x10.temperatureReadAt),
      lastHeartbeat: toMs(x10.lastHeartbeat),
      lastMounted: toMs(x10.lastMounted),
      lastUsed: toMs(x10.lastUsed),
      hasKeg: !!keg,
      volume: keg?.volume ? keg.volume / 1000 : X10_KEG_VOLUME_LITRES,
      volumeLeft: keg?.volumeLeft ? Math.max(keg.volumeLeft, 0) / 1000 : 0,
      beverageId: keg?.beverageKnown ? keg.beverageId || undefined : undefined,
      beverageKnown: !!keg?.beverageKnown,
      bestBefore: toMs(keg?.bestBefore),
      hasNfcBeverage: !!keg?.hasNfcBeverage,
      beverage: new BeverageTranslation(),
      firmware: x10.firmware || undefined,
      targetFirmware: firmwareLabel(x10.targetBinary || undefined),
      updateAvailable: isFirmwareUpdateAvailable(
        x10.firmware || undefined,
        x10.targetBinary || undefined
      ),
      lastRssi: x10.lastRssi ?? undefined,
      health: x10.health
    };
    const evaluation = evaluateX10(mapped, now);
    return { ...mapped, evaluation, status: evaluation.level };
  });
